import { ComandaItemRes } from '@core/interfaces/request-response/comanda.dto';

interface IBuildPrecuentaImagenInput {
  mesaNombre: string;
  fechaVenta: Date;
  productos: ComandaItemRes[];
}

const CANVAS_WIDTH = 760;
const PADDING_X = 28;
const TOP_PADDING = 24;
const BOTTOM_PADDING = 56;
const ROW_BASE_HEIGHT = 32;

const formatMoney = (value: number): string => {
  return Number(value || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const sanitizeFileName = (value: string): string => {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9-_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
  if (!text) return [''];
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = words[0] || '';

  for (let i = 1; i < words.length; i += 1) {
    const testLine = `${currentLine} ${words[i]}`;
    if (ctx.measureText(testLine).width <= maxWidth) {
      currentLine = testLine;
      continue;
    }
    lines.push(currentLine);
    currentLine = words[i];
  }

  lines.push(currentLine);
  return lines;
};

export const buildPrecuentaImagenFile = async (data: IBuildPrecuentaImagenInput): Promise<File> => {
  const { mesaNombre, fechaVenta, productos } = data;

  const tempCanvas = document.createElement('canvas');
  const tempCtx = tempCanvas.getContext('2d');
  if (!tempCtx) throw new Error('No se pudo inicializar canvas');

  tempCtx.font = '600 16px Arial';
  const productColumnMaxWidth = CANVAS_WIDTH - 110 - 180;

  const productRows = productos.map((producto) => {
    const nombre = producto.nombre_producto || 'Producto';
    const wrappedName = wrapText(tempCtx, nombre, productColumnMaxWidth);
    const rowHeight = Math.max(ROW_BASE_HEIGHT, wrappedName.length * 24 + 12);

    return {
      cantidad: `${producto.cantidad}x`,
      subtotal: Number(producto.subtotal || 0),
      lines: wrappedName,
      rowHeight,
    };
  });

  const rowsHeight = productRows.reduce((acc, row) => acc + row.rowHeight, 0);
  const headerHeight = 110;
  const tableHeaderHeight = 56;
  const totalSectionHeight = 96;
  const footerHeight = 56;
  const canvasHeight =
    TOP_PADDING +
    headerHeight +
    tableHeaderHeight +
    rowsHeight +
    totalSectionHeight +
    footerHeight +
    BOTTOM_PADDING;

  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_WIDTH;
  canvas.height = Math.ceil(canvasHeight);

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No se pudo obtener contexto de dibujo');
  ctx.textBaseline = 'top';

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  let y = TOP_PADDING;

  ctx.fillStyle = '#0f172a';
  ctx.textAlign = 'center';
  ctx.font = '700 48px Arial';
  ctx.fillText(`PRECUENTA - ${mesaNombre}`, canvas.width / 2, y);

  ctx.fillStyle = '#334155';
  ctx.font = '600 24px Arial';
  ctx.fillText(fechaVenta.toLocaleString('es-PE'), canvas.width / 2, y + 56);

  y += headerHeight;

  ctx.fillStyle = '#111827';
  ctx.font = '700 28px Arial';
  ctx.textAlign = 'left';
  ctx.fillText('Cant.', PADDING_X, y);
  ctx.fillText('Producto', 110, y);
  ctx.textAlign = 'right';
  ctx.fillText('Sub Total', canvas.width - PADDING_X, y);

  ctx.strokeStyle = '#111827';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(PADDING_X, y + 36);
  ctx.lineTo(canvas.width - PADDING_X, y + 36);
  ctx.stroke();

  y += tableHeaderHeight;

  ctx.font = '600 24px Arial';
  ctx.fillStyle = '#111827';

  productRows.forEach((row) => {
    ctx.textAlign = 'left';
    ctx.fillText(row.cantidad, PADDING_X, y + 2);

    row.lines.forEach((line, index) => {
      ctx.fillText(line, 110, y + 2 + index * 24);
    });

    ctx.textAlign = 'right';
    ctx.fillText(`S/. ${formatMoney(row.subtotal)}`, canvas.width - PADDING_X, y + 2);

    y += row.rowHeight;
  });

  const total = productos.reduce((acc, item) => acc + Number(item.subtotal || 0), 0);

  ctx.strokeStyle = '#111827';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(PADDING_X, y + 8);
  ctx.lineTo(canvas.width - PADDING_X, y + 8);
  ctx.stroke();

  y += 22;

  ctx.textAlign = 'right';
  ctx.font = '700 52px Arial';
  ctx.fillStyle = '#0f172a';
  ctx.fillText(`TOTAL: S/. ${formatMoney(total)}`, canvas.width - PADDING_X, y);

  y += 64;

  ctx.font = '600 20px Arial';
  ctx.fillStyle = '#475569';
  ctx.fillText(
    'Este documento es una precuenta y no constituye comprobante fiscal.',
    canvas.width - PADDING_X,
    y,
  );

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/png', 1);
  });

  if (!blob) throw new Error('No se pudo convertir la imagen');

  const mesaSafe = sanitizeFileName(mesaNombre || 'delivery') || 'delivery';
  const stamp = new Date(fechaVenta).toISOString().replace(/[:.]/g, '-');
  return new File([blob], `precuenta-${mesaSafe}-${stamp}.png`, { type: 'image/png' });
};
